import React, { memo, useState } from "react";
import { Checkbox } from "@veneer/core";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { ThemeProvider, Theme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { WeekDayKey, DaySchedule } from "../../hooks/weeklyAgenda/useWeeklyAgenda";

interface DayScheduleRowProps {
  day: WeekDayKey;
  label: string;
  schedule: DaySchedule;
  theme: Theme;
  labelStartTime: string;
  labelEndTime: string;
  onToggle: (day: WeekDayKey) => void;
  onStartTimeChange: (day: WeekDayKey, time: moment.Moment | null) => void;
  onEndTimeChange: (day: WeekDayKey, time: moment.Moment | null) => void;
  minStartTime?: moment.Moment;
}

const DayScheduleRow: React.FC<DayScheduleRowProps> = ({
  day,
  label,
  schedule,
  theme,
  labelStartTime,
  labelEndTime,
  onToggle,
  onStartTimeChange,
  onEndTimeChange,
  minStartTime,
}) => {
  const { enabled, startTime, endTime } = schedule;
  const [t] = useTranslation("global");
  const [startTimeErrorReason, setStartTimeErrorReason] = useState<string | null>(null);
  const [endTimeErrorReason, setEndTimeErrorReason] = useState<string | null>(null);

  const timesAreEqual =
    startTime !== null && moment(startTime).isValid() &&
    endTime !== null && moment(endTime).isValid() &&
    moment(endTime).isSame(moment(startTime), 'minute');

  // Direct comparison so validation stays correct when startTime changes after endTime is set
  const endIsBeforeStart =
    startTime !== null && moment(startTime).isValid() &&
    endTime !== null && moment(endTime).isValid() &&
    moment(endTime).isBefore(moment(startTime), 'minute');

  const endTimeInvalid = timesAreEqual || endIsBeforeStart;

  // Explicitly disable clock hours that fall entirely before startTime + 5 min.
  // This ensures the visual graying on the 24h clock (inner/outer ring) is always clear,
  // complementing the minTime prop which handles minute-level disabling.
  const endShouldDisableTime = React.useCallback(
    (value: number, clockType: string) => {
      // Use format-aware parse: plain 'H:mm' strings fail moment() without a format
      const st = moment.isMoment(startTime) ? startTime : moment(String(startTime), ['HH:mm', 'H:mm']);
      if (!startTime || !st.isValid()) return false;
      if (clockType === 'hours') {
        const minTotalMins = st.hours() * 60 + st.minutes() + 5;
        return (value * 60 + 59) < minTotalMins;
      }
      return false;
    },
    [startTime]
  );

  let endTimeErrorMessage = t("header.endTimeInvalidError");
  if (timesAreEqual) {
    endTimeErrorMessage = t("header.startEndTimeSameError");
  } else if (endTimeErrorReason === 'minTime' || endIsBeforeStart) {
    endTimeErrorMessage = t("header.endTimeBeforeStartError");
  }

  return (
    <div className="weeklyAgendaDayRow">
      <Checkbox
        className="weeklyAgendaDayCheckbox"
        label={label}
        checked={enabled}
        onChange={() => onToggle(day)}
      />

      <div className="weeklyAgendaDayTimes">
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <ThemeProvider theme={theme}>
            {/* Unified error block spanning both columns — mirrors AgendaCreationBlock layout */}
            {enabled && (startTimeErrorReason || endTimeErrorReason || endTimeInvalid) && (
              <div className="agendaTimesErrorBlock" style={{ gridColumn: '1 / -1', width: '100%', marginTop: 0 }}>
                {startTimeErrorReason && (
                  <p className="agendaTimePickerError">
                    {startTimeErrorReason === 'minTime'
                      ? t("header.startTimePastError")
                      : t("header.startTimeInvalidError")}
                  </p>
                )}
                {(endTimeErrorReason || endTimeInvalid) && (
                  <p className="agendaTimePickerError">{endTimeErrorMessage}</p>
                )}
              </div>
            )}
            <div className="agendaTimePickerCell">
              <TimePicker
                className="createAgendaTimePicker"
                label={labelStartTime}
                value={enabled ? startTime : null}
                disabled={!enabled}
                onChange={(value) => onStartTimeChange(day, value as moment.Moment | null)}
                onError={(reason) => setStartTimeErrorReason(reason ?? null)}
                renderInput={(params) => <TextField {...params} />}
                minutesStep={5}
                ampm={false}
                minTime={enabled && minStartTime ? minStartTime : undefined}
              />
            </div>

            <div className="agendaTimePickerCell">
              <TimePicker
                key={startTime ? `end-${moment(startTime).format('HH:mm')}` : 'end-empty'}
                className="createAgendaTimePicker"
                label={labelEndTime}
                value={enabled ? endTime : null}
                disabled={!enabled || !startTime}
                minTime={startTime ? moment(startTime).add(5, 'minutes') : undefined}
                shouldDisableTime={endShouldDisableTime}
                onChange={(value) => onEndTimeChange(day, value as moment.Moment | null)}
                onError={(reason) => setEndTimeErrorReason(reason ?? null)}
                renderInput={(params) => (
                  <TextField {...params} error={params.error || endTimeInvalid} />
                )}
                minutesStep={5}
                ampm={false}
              />
            </div>
          </ThemeProvider>
        </LocalizationProvider>
      </div>
    </div>
  );
};

export default memo(DayScheduleRow);
